<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  ApiError,
  HousingApplication,
  HousingOrgRole,
  HousingOrgRoleName,
  LoginAccountInSysOrgDto,
  SysOrgRoleName,
} from "~/api";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  housingApplication: HousingApplication;
  disabled?: boolean;
}>();

const emit =
  defineEmits<
    (e: "update:housingApplication", value: HousingApplication) => void
  >();

const myInfoStore = useMyInfoStore();
const loginAccountStore = useLoginAccountsStore();
const filteredHMBUsers = computed<LoginAccountInSysOrgDto[]>(() =>
  loginAccountStore.List.filter(
    (l) =>
      l.sysOrgRoles?.includes(SysOrgRoleName.MEMBER) &&
      housingOrgRoles.value[l.userGuid]?.some(
        (role) => role.roleName === HousingOrgRoleName.INTAKE,
      ),
  ),
);

const housingOrgRoles = ref<Record<string, HousingOrgRole[]>>({});

const loadHousingOrgRoles = async () => {
  housingOrgRoles.value = await apiClient.housingOrgRoles.getHousingOrgRoles(
    myInfoStore.currentSysOrgGuid,
  );
};

onMounted(async () => {
  await myInfoStore.load();
  await Promise.all([loginAccountStore.load(), loadHousingOrgRoles()]);
});

const accountsForAssignTo = computed(() => {
  const result = filteredHMBUsers.value.map((item) => ({
    user: item,
    label:
      item.loginName && item.email
        ? `${item.loginName} (${item.email})`
        : (item.loginName ?? item.email),
    value: item.userGuid,
  }));
  const currentUserIndex = result.findIndex(
    (item) => item.value === myInfoStore.userInfo?.userGuid,
  );

  // Move current user to top of select list (only if found and not already first)
  if (currentUserIndex > 0) {
    result.unshift(result.splice(currentUserIndex, 1)[0]);
  }

  return result;
});

const handleAssignedTo = async (newGuid: string) => {
  await apiClient.housingApplication
    .updateHousingApplication(props.housingApplication.guid, {
      ...props.housingApplication,
      assignedTo: newGuid,
    })
    .then((updatedApplication) => {
      emit("update:housingApplication", updatedApplication);

      if (
        updatedApplication.assignedTo === null ||
        updatedApplication.assignedTo === undefined
      ) {
        ElMessage.success("Unassigned");
        return;
      }
      const assignedToName = loginAccountStore.getName(
        updatedApplication.assignedTo,
      );
      ElMessage.success("Assigned to " + assignedToName);
    })
    .catch((error: ApiError) => {
      showError(error);
    });
};
</script>

<template>
  <el-select-v2
    :model-value="housingApplication.assignedTo"
    filterable
    :options="accountsForAssignTo"
    clearable
    :disabled="disabled"
    :value-on-clear="undefined"
    :empty-values="[null, undefined]"
    placeholder="Assign to..."
    @change="
      (newGuid: string) => {
        handleAssignedTo(newGuid);
      }
    "
  >
    <template #default="{ item }">
      <span v-if="myInfoStore.userInfo?.userGuid === item.value">
        <b>Me</b>
        ({{ item.label }})</span
      >
      <span v-else>{{ item.label }}</span>
    </template>
  </el-select-v2>
</template>
